import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import type { RouteProp } from '@react-navigation/native';
import { useFocusEffect } from '@react-navigation/native';
import {
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import type { AddressType } from '@sweetgreen/garnish';
import { useBreakpointHelpers } from '@sweetgreen/garnish';

import { LocationsMap } from '@order/components';

import { TransparentModal } from '../../../../components/TransparentModal';
import type { ModalStackParamList } from '../../../../navigation/AppNavigation.props';
import { LocationLayout } from '../../../LocationsScreen/components';
import { useLocationsScreenData } from '../../../LocationsScreen/LocationsScreen.data';
import { useLocationConfirm } from '../../hooks';
import { LeftIcon, LocationTypePickerHeader, RightIcon } from './subcomponents';

export const OrderProductSearchLocationScreen = () => {
  const { params } =
    useRoute<RouteProp<ModalStackParamList, 'OrderProductSearchLocation'>>();
  const { productSlug: baseProductSlug } = params ?? {};

  const isFocused = useIsFocused();
  const navigation = useNavigation();

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.getParent()?.goBack();
  }, [navigation]);

  const handleDismiss = useCallback(() => {
    navigation.getParent()?.goBack();
  }, [navigation]);

  const { isMobile } = useBreakpointHelpers();

  // ─────────────────────────────────────────────────────────────────────

  const [selectedLocationData, setSelectedLocationData] =
    useState<SelectedLocation>();

  const {
    isFetchingProduct,
    isProductNotAvailable,
    isAddingLineItemToCart,
    handleLocationConfirm,
  } = useLocationConfirm({
    baseProductSlug,
    restaurantId: selectedLocationData?.restaurantId,
    restaurantSlug: selectedLocationData?.restaurantSlug,
    deliveryAddressId: selectedLocationData?.deliveryOrderDetailAddressId,
    deliveryVendorName: selectedLocationData?.deliveryOrderDetailVendorName,
    deliveryVendorRestaurantId:
      selectedLocationData?.deliveryOrderDetailVendorRestaurantId,
    deliveryFee: selectedLocationData?.deliveryOrderDetailDeliveryFee,
  });

  const onOpenRestaurant = useCallback<OnOpenRestaurant>((props) => {
    setSelectedLocationData({
      restaurantId: props?.restaurantId,
      restaurantSlug: props?.restaurantSlug,
    });
  }, []);

  const onOpenDeliveryRestaurant = useCallback<OnOpenDeliveryRestaurant>(
    (props) => {
      setSelectedLocationData({
        restaurantId: props?.restaurantId,
        restaurantSlug: props?.restaurantSlug,
        deliveryOrderDetailAddressId: props?.deliveryOrderDetail?.addressId,
        deliveryOrderDetailVendorName: props?.deliveryOrderDetail?.vendor,
        deliveryOrderDetailVendorRestaurantId:
          props?.deliveryOrderDetail?.vendorRestaurantId,
        deliveryOrderDetailDeliveryFee: props?.deliveryOrderDetail?.deliveryFee,
      });
    },
    [],
  );

  const locationScreenData = useLocationsScreenData({
    onOpenRestaurant,
    onOpenDeliveryRestaurant,
  });

  // ─────────────────────────────────────────────────────────────────────

  const {
    locationSearchType,
    isAddingAddress,
    setLocationSearchType,
    setSelectedLocation,
  } = locationScreenData;

  const handleLocationTypeSelect = React.useCallback(
    (searchType: AddressType) => {
      setLocationSearchType(searchType);
      setSelectedLocation('');
    },
    [setLocationSearchType, setSelectedLocation],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    navigation.setOptions({
      // eslint-disable-next-line react/no-unstable-nested-components
      headerTitle: () => (
        <LocationTypePickerHeader
          locationSearchType={locationSearchType}
          onSelectLocationType={handleLocationTypeSelect}
        />
      ),
      headerLeft: isMobile
        ? () => <LeftIcon onPress={handleGoBack} />
        : undefined,
      headerRight: isMobile
        ? () => <RightIcon onPress={handleDismiss} />
        : undefined,
      headerShown: isMobile,
    });
  }, [
    navigation,
    isMobile,
    locationSearchType,
    handleGoBack,
    handleLocationTypeSelect,
    handleDismiss,
  ]);

  // navigate to the selected product details screen if it's available
  useEffect(() => {
    const isProductAvailable = selectedLocationData && !isFetchingProduct;

    if (isProductAvailable) {
      void handleLocationConfirm();
    }
  }, [
    isFetchingProduct,
    isProductNotAvailable,
    handleLocationConfirm,
    selectedLocationData,
  ]);

  // Reset state when navigating away
  useFocusEffect(
    useCallback(
      () => () => {
        setSelectedLocationData(undefined);
      },
      [],
    ),
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!isFocused) return null;

  return (
    <TransparentModal
      testID="change-location"
      disableAnimation={isMobile}
      size="large"
      onClose={handleDismiss}
    >
      <LocationLayout
        locationSearchTypePicker={
          <LocationTypePickerHeader
            locationSearchType={locationSearchType}
            onSelectLocationType={handleLocationTypeSelect}
            onBack={handleGoBack}
            onClose={handleDismiss}
          />
        }
        {...locationScreenData}
        loading={isFetchingProduct || isAddingLineItemToCart}
        isAddingAddress={
          isAddingAddress || isFetchingProduct || isAddingLineItemToCart
        }
        withoutFooter
      >
        <LocationsMap {...locationScreenData} />
      </LocationLayout>
    </TransparentModal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OnOpenRestaurant = (props?: {
  readonly restaurantId?: string;
  readonly restaurantSlug?: string;
}) => void;

type OnOpenDeliveryRestaurant = (props?: {
  readonly deliveryOrderDetail?: {
    readonly addressId?: string;
    readonly vendor?: string;
    readonly vendorRestaurantId?: string;
    readonly deliveryFee?: number;
  };
  readonly restaurantId?: string;
  readonly restaurantSlug?: string;
}) => void;

type SelectedLocation = {
  readonly deliveryOrderDetailAddressId?: string;
  readonly restaurantId?: string;
  readonly restaurantSlug?: string;
  readonly deliveryOrderDetailVendorName?: string;
  readonly deliveryOrderDetailVendorRestaurantId?: string;
  readonly deliveryOrderDetailDeliveryFee?: number;
};
